import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ModernLogo } from "@/components/ModernLogo";
import { 
  CheckCircle, 
  XCircle, 
  Percent, 
  Calculator,
  TrendingUp,
  ArrowLeft,
  Award,
  DollarSign,
  CreditCard,
  Users,
  Clock,
  Shield
} from "lucide-react";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";

export function Pricing() {
  const features = [
    {
      icon: <XCircle className="w-6 h-6 text-green-600" />,
      title: "No Monthly Fees",
      description: "Zero upfront costs, no recurring subscriptions"
    },
    {
      icon: <XCircle className="w-6 h-6 text-green-600" />,
      title: "No Setup Fees",
      description: "Start immediately without any initial payments"
    },
    {
      icon: <XCircle className="w-6 h-6 text-green-600" />,
      title: "No Contract Lock-ins",
      description: "Complete freedom to join or leave anytime"
    },
    {
      icon: <CheckCircle className="w-6 h-6 text-primary" />,
      title: "7% Commission Only",
      description: "Industry's lowest rate - pay only when you earn"
    },
    {
      icon: <CheckCircle className="w-6 h-6 text-primary" />,
      title: "Instant Payments",
      description: "Get paid immediately after job completion"
    },
    {
      icon: <CheckCircle className="w-6 h-6 text-primary" />,
      title: "Unlimited Job Browsing",
      description: "View and apply to as many jobs as you want"
    }
  ];

  const comparisons = [
    {
      platform: "MyBuilder",
      monthlyFee: "Unlimited",
      commission: "0%",
      totalCost: "Unlimited/year",
      highlight: false
    },
    {
      platform: "Checkatrade", 
      monthlyFee: "£150+",
      commission: "0%",
      totalCost: "£1,800+/year",
      highlight: false
    },
    {
      platform: "TaskRabbit",
      monthlyFee: "£0",
      commission: "15%",
      totalCost: "£3,600/year*",
      highlight: false
    },
    {
      platform: "Our Platform",
      monthlyFee: "£0", 
      commission: "7%",
      totalCost: "£1,680/year*",
      highlight: true
    }
  ];

  const earningsExamples = [
    {
      monthlyEarnings: "£1,000",
      ourFee: "£70",
      myBuilderCost: "Unlimited",
      checkatradeCost: "£150",
      taskRabbitFee: "£150",
      yourKeep: "£930"
    },
    {
      monthlyEarnings: "£2,000", 
      ourFee: "£140",
      myBuilderCost: "Unlimited",
      checkatradeCost: "£150",
      taskRabbitFee: "£300",
      yourKeep: "£1,860"
    },
    {
      monthlyEarnings: "£3,000",
      ourFee: "£210", 
      myBuilderCost: "Unlimited",
      checkatradeCost: "£150",
      taskRabbitFee: "£450",
      yourKeep: "£2,790"
    },
    {
      monthlyEarnings: "£5,000",
      ourFee: "£350",
      myBuilderCost: "Unlimited", 
      checkatradeCost: "£150",
      taskRabbitFee: "£750",
      yourKeep: "£4,650"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <SEO
        title="Pricing - Transparent Costs for Home Services | Sortable | Only 7% Commission"
        description="View transparent pricing for all home services on Sortable. No hidden fees, competitive rates from verified local tradesmen. Only pay 7% commission on completed jobs."
        keywords="home services pricing, tradesman costs, contractor rates, transparent pricing, no hidden fees, competitive quotes, fair pricing, cost estimates, service charges, hourly rates, fixed price jobs, material costs, labour costs, call out charges, emergency rates, weekend pricing, holiday surcharges, bulk discounts, repeat customer discounts, seasonal pricing, regional variations, price comparison, budget planning, cost calculator, payment terms, deposit requirements, milestone payments, completion payments, invoice processing, tax inclusive pricing, VAT rates, insurance costs, permit fees, disposal charges, travel expenses, consultation fees, survey costs, design fees, project management costs, warranty coverage, aftercare services, maintenance packages, affordable tradesmen, cheap contractors, low cost repairs, budget home services, economical maintenance, value for money, best price guarantee, competitive rates, market rates, industry standards, cost breakdown, pricing structure, commission rates, platform fees, service costs"
        canonical="https://sortable.lovable.app/pricing"
        ogImage="https://sortable.lovable.app/hero-tradesman.jpg"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Sortable Home Services Pricing",
          "description": "Transparent pricing for home services with only 7% commission",
          "provider": {
            "@type": "Organization",
            "name": "Sortable"
          },
          "offers": {
            "@type": "Offer",
            "priceCurrency": "GBP",
            "description": "7% commission on completed jobs, no monthly fees"
          }
        }}
      />
      {/* Header */}
      <header className="bg-background/95 backdrop-blur-sm border-b border-border/50 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-2 md:py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <ModernLogo showTagline={false} />
            </Link>
            <div className="flex items-center gap-1 md:gap-4">
              <Link to="/for-tradesmen">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="h-3 w-3 md:h-4 md:w-4 md:mr-2" />
                  <span className="hidden md:inline">Back to For Tradesmen</span>
                  <span className="md:hidden sr-only">Back</span>
                </Button>
              </Link>
              <Link to="/">
                <Button variant="default" size="sm">
                  <span className="md:hidden">Start</span>
                  <span className="hidden md:inline">Get Started</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 px-4">
        <div className="container mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-gradient-hero/10 border border-primary/20 text-primary px-6 py-3 rounded-full text-lg font-semibold mb-8 shadow-elegant">
            <Award className="h-5 w-5" />
            Transparent Pricing
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-hero bg-clip-text text-transparent">
            Pay Only When You Work
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            The fairest pricing in the industry. No hidden fees, no monthly charges, 
            just a simple 7% commission on completed jobs.
          </p>
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-6 max-w-md mx-auto">
            <div className="flex items-center justify-center gap-2 text-2xl font-bold text-primary">
              <Percent className="w-8 h-8" />
              <span>7% Commission</span>
            </div>
            <p className="text-muted-foreground mt-2">Only on completed jobs</p>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 px-4 bg-background/50">
        <div className="container mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Everything Included at No Extra Cost
            </h2>
            <p className="text-lg text-muted-foreground">
              All the tools and features you need to grow your business.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border-border/50 text-center hover:shadow-elegant transition-all duration-300">
                <CardHeader>
                  <div className="flex justify-center mb-4">
                    {feature.icon}
                  </div>
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Calculator */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Calculate Your Savings
            </h2>
            <p className="text-lg text-muted-foreground mb-4">
              See how much you could save compared to other platforms.
            </p>
            <Link to="/why-us">
              <Button variant="link" className="text-primary hover:text-primary/80">
                <Shield className="h-4 w-4 mr-2" />
                Learn why our pricing is fairer for everyone →
              </Button>
            </Link>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full bg-background rounded-lg border border-border/50 shadow-elegant">
              <thead>
                <tr className="border-b border-border/50">
                  <th className="text-left p-4 font-semibold">Monthly Earnings</th>
                  <th className="text-left p-4 font-semibold">Our Fee (7%)</th>
                  <th className="text-left p-4 font-semibold">MyBuilder</th>
                  <th className="text-left p-4 font-semibold">Checkatrade</th>
                  <th className="text-left p-4 font-semibold">TaskRabbit (15%)</th>
                  <th className="text-left p-4 font-semibold text-primary">You Keep</th>
                </tr>
              </thead>
              <tbody>
                {earningsExamples.map((example, index) => (
                  <tr key={index} className="border-b border-border/50">
                    <td className="p-4 font-medium">{example.monthlyEarnings}</td>
                    <td className="p-4 text-green-600 font-medium">{example.ourFee}</td>
                    <td className="p-4 text-destructive">{example.myBuilderCost}</td>
                    <td className="p-4 text-destructive">{example.checkatradeCost}</td>
                    <td className="p-4 text-destructive">{example.taskRabbitFee}</td>
                    <td className="p-4 text-primary font-bold">{example.yourKeep}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 p-6 bg-primary/5 rounded-lg border border-primary/20">
            <div className="flex items-start gap-4">
              <Calculator className="w-6 h-6 text-primary mt-1" />
              <div>
                <h3 className="font-semibold mb-2">Annual Savings Example</h3>
                <p className="text-muted-foreground">
                  A tradesman earning £2,000/month saves over £1,000 annually compared to other platforms:
                  <br />• Our platform: £1,680/year in fees
                  <br />• Competitors: £1,800-£3,600+/year in fees
                  <br />• <strong className="text-primary">Your savings: £120-£1,920 per year</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 px-4 bg-background/50">
        <div className="container mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Platform Comparison
            </h2>
            <p className="text-lg text-muted-foreground">
              See how we stack up against the competition.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full bg-background rounded-lg border border-border/50 shadow-elegant">
              <thead>
                <tr className="border-b border-border/50">
                  <th className="text-left p-4 font-semibold">Platform</th>
                  <th className="text-left p-4 font-semibold">Monthly Fee</th>
                  <th className="text-left p-4 font-semibold">Commission</th>
                  <th className="text-left p-4 font-semibold">Annual Cost*</th>
                </tr>
              </thead>
              <tbody>
                {comparisons.map((comparison, index) => (
                  <tr 
                    key={index} 
                    className={`border-b border-border/50 ${comparison.highlight ? 'bg-primary/5' : ''}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${comparison.highlight ? 'text-primary' : ''}`}>
                          {comparison.platform}
                        </span>
                        {comparison.highlight && (
                          <Award className="w-4 h-4 text-primary" />
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={comparison.monthlyFee === '£0' ? 'text-green-600 font-medium' : 'text-destructive'}>
                        {comparison.monthlyFee}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={comparison.commission === '7%' ? 'text-green-600 font-medium' : ''}>
                        {comparison.commission}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={comparison.highlight ? 'text-primary font-bold' : 'text-muted-foreground'}>
                        {comparison.totalCost}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-muted-foreground mt-4 text-center">
            *Based on £2,000/month earnings
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Pricing Questions
            </h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            {[
              {
                question: "When do I pay the 7% commission?",
                answer: "You only pay when you complete a job and receive payment from the customer. No job completion means no fee."
              },
              {
                question: "Are there any hidden fees?",
                answer: "Absolutely not. The 7% commission is the only fee. No setup costs, monthly fees, or additional charges."
              },
              {
                question: "How is the commission calculated?",
                answer: "Commission is 7% of the agreed job price. For example, on a £1,000 job, you pay £70 and keep £930."
              },
              {
                question: "Can I leave the platform anytime?",
                answer: "Yes, there are no contracts or commitments. You can stop using the platform at any time with no penalties."
              },
              {
                question: "How do I receive payments?",
                answer: "Payments are processed immediately after job completion and transferred to your bank account within 24 hours."
              }
            ].map((faq, index) => (
              <Card key={index} className="border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg">{faq.question}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 px-4 bg-gradient-hero">
        <div className="container mx-auto text-center text-primary-foreground">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Start Earning with Fair Pricing Today
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            No setup fees, no monthly charges, no contracts. Just fair commission on work you complete.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/">
              <Button size="lg" variant="secondary" className="bg-white text-primary hover:bg-white/90">
                Join Now - It's Free
              </Button>
            </Link>
            <Link to="/why-us">
              <Button size="lg" variant="outline" className="bg-transparent text-white border-white hover:bg-white/10">
                <Shield className="h-5 w-5 mr-2" />
                See All Guarantees
              </Button>
            </Link>
          </div>
          <p className="text-sm mt-4 opacity-75">
            Start immediately • No credit card required • Fair pricing guaranteed
          </p>
        </div>
      </section>
    </div>
  );
}